import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Checkbox,
  Typography,
  Chip,
  Alert,
} from '@mui/material';

const SlotSelector = ({ availableSlots, selectedSlots, handleSlotToggle }) => {
  const isSlotSelected = (slot) => {
    return selectedSlots.some(
      (s) => s.screenId === slot.screenId && s.slotNumber === slot.slotNumber
    );
  };

  if (!availableSlots || availableSlots.length === 0) {
    return (
      <Alert severity="warning">
        No slots found. Please check your date range and location.
      </Alert>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Box display="flex" gap={3} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 24,
                height: 24,
                backgroundColor: '#c8e6c9',
                border: '2px solid #4caf50',
                borderRadius: 1,
              }}
            />
            <Typography variant="body2">
              <strong>Available</strong> - Ready to book
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 24,
                height: 24,
                backgroundColor: '#ffcccc',
                border: '2px solid #f44336',
                borderRadius: 1,
              }}
            />
            <Typography variant="body2">
              <strong>Filled</strong> - Already booked (read-only)
            </Typography>
          </Box>
        </Box>
      </Alert>

      <Grid container spacing={2}>
        {availableSlots.map((slot, index) => {
          const isAvailable = slot.available;
          const isSelected = isSlotSelected(slot);

          return (
            <Grid item xs={12} sm={6} md={4} key={`${slot.screenId}-${slot.slotNumber}-${index}`}>
              <Card
                sx={{
                  cursor: isAvailable ? 'pointer' : 'not-allowed',
                  border: '2px solid',
                  borderColor: isAvailable
                    ? isSelected
                      ? '#4caf50'
                      : '#e0e0e0'
                    : '#f44336',
                  backgroundColor: isAvailable
                    ? isSelected
                      ? '#f1f8f4'
                      : '#ffffff'
                    : '#ffebee',
                  opacity: isAvailable ? 1 : 0.7,
                  transition: 'all 0.2s ease',
                  '&:hover': isAvailable
                    ? {
                        borderColor: '#4caf50',
                        boxShadow: '0 4px 8px rgba(76, 175, 80, 0.2)',
                      }
                    : {},
                }}
                onClick={() => isAvailable && handleSlotToggle(slot)}
              >
                <CardContent>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {slot.screenName || `Screen ${slot.screenId}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Slot {slot.slotNumber}
                      </Typography>
                      {slot.siteCode && (
                        <Typography variant="caption" color="text.secondary">
                          Site: {slot.siteCode}
                        </Typography>
                      )}
                      <Box mt={1}>
                        <Chip
                          label={isAvailable ? 'Available' : 'Filled'}
                          color={isAvailable ? 'success' : 'error'}
                          size="small"
                          variant={isAvailable ? 'outlined' : 'filled'}
                        />
                      </Box>
                    </Box>
                    {isAvailable && (
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSlotToggle(slot)}
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {selectedSlots.length > 0 && (
        <Alert severity="success" sx={{ mt: 3 }}>
          {selectedSlots.length} slot(s) selected
        </Alert>
      )}
    </Box>
  );
};

export default SlotSelector;
